import React, { useState, useMemo, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Heart, ArrowLeft, CheckCircle, Shield, Lock, DollarSign, Star, Users, Target, Award, CreditCard } from "lucide-react";
import SEO from "./SEO.jsx";
import { useAuth } from "./AuthContext";
import { BACKEND_URL as GREENWICH_BACKEND_URL } from '../utils/network.js';
import "./Donate.css";

import { toast } from '../utils/toast.js';

const API_URL = GREENWICH_BACKEND_URL;

const Donate = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [donationAmount, setDonationAmount] = useState("");
  const [customAmount, setCustomAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [donationSuccess, setDonationSuccess] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [vposEnabled, setVposEnabled] = useState(false);
  const [cardPaying, setCardPaying] = useState(false);
  const paymentResult = searchParams.get("payment");

  const MINIMUM_AMOUNT_USD = 3;
  const MINIMUM_AMOUNT_AMD = 1250;

  const donationTiers = useMemo(() => [
    { id: 1, name: t("pages.donate.tier1n"), amount: 3, description: t("pages.donate.tier1d"), perks: [t("pages.donate.tier1p0"), t("pages.donate.tier1p1")], color: "#5ecb7c", popular: false },
    { id: 2, name: t("pages.donate.tier2n"), amount: 5, description: t("pages.donate.tier2d"), perks: [t("pages.donate.tier2p0"), t("pages.donate.tier2p1"), t("pages.donate.tier2p2")], color: "#5aa8ff", popular: true },
    { id: 3, name: t("pages.donate.tier3n"), amount: 10, description: t("pages.donate.tier3d"), perks: [t("pages.donate.tier3p0"), t("pages.donate.tier3p1"), t("pages.donate.tier3p2")], color: "#c08fff", popular: false },
    { id: 4, name: t("pages.donate.tier4n"), amount: 25, description: t("pages.donate.tier4d"), perks: [t("pages.donate.tier4p0"), t("pages.donate.tier4p1"), t("pages.donate.tier4p2")], color: "#ffcb47", popular: false },
  ], [t]);

  const whySupportItems = useMemo(() => t("pages.donate.whyItems", { returnObjects: true }) || [], [t]);

  useEffect(() => {
    fetch(`${API_URL}/api/payments/vpos/status`)
      .then((res) => res.json())
      .then((data) => { if (data.success) setVposEnabled(!!data.enabled); })
      .catch(() => {});
  }, []);

  // Clear ?payment=... from the URL once shown, so a refresh doesn't re-show the banner.
  useEffect(() => {
    if (!paymentResult) return;
    const next = new URLSearchParams(searchParams);
    next.delete("payment");
    setSearchParams(next, { replace: true });
  }, [paymentResult]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTierSelect = (tier) => { setSelectedTier(tier.id); setDonationAmount(tier.amount.toString()); setCustomAmount(""); };
  const handleCustomAmountChange = (e) => { const v = e.target.value.replace(/[^0-9]/g, ""); setCustomAmount(v); if (v) { setDonationAmount(v); setSelectedTier(null); } };
  const handleInputChange = (e) => { const { name, value } = e.target; setFormData(prev => ({ ...prev, [name]: value })); };

  const handlePaymentMethodSelect = (method) => {
    setPaymentMethod(method);
    const el = document.createElement("div");
    el.style.cssText = "position:fixed;top:20px;right:20px;background:#1e3a2c;color:#fdfdf9;border:1px solid rgba(255,203,71,0.4);padding:12px 20px;border-radius:8px;z-index:10000;box-shadow:0 8px 24px rgba(0,0,0,0.35);font-family:inherit;font-size:14px;";
    el.textContent = t("pages.donate.bmcToast");
    document.body.appendChild(el);
    setTimeout(() => document.body.removeChild(el), 3000);
  };

  const handlePayWithCard = async (amountAmd) => {
    if (!user) { toast(t("pages.donate.alertCardLogin")); return; }
    if (cardPaying) return;
    setCardPaying(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/api/payments/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ purpose: "donation", amount: amountAmd, lang: i18n.language }),
      });
      const data = await res.json();
      if (data.success && data.redirectUrl) {
        window.location.href = data.redirectUrl;
      } else {
        toast(data.error || t("pages.donate.alertCardError"));
        setCardPaying(false);
      }
    } catch {
      toast(t("pages.donate.alertCardError"));
      setCardPaying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!donationAmount || parseFloat(donationAmount) < MINIMUM_AMOUNT_USD) { toast(t("pages.donate.alertMin", { min: MINIMUM_AMOUNT_USD })); return; }
    if (!paymentMethod) { toast(t("pages.donate.alertPayment")); return; }

    if (paymentMethod === "card") {
      const amountAmd = Math.max(100, Math.round(totalAmount * 416.67));
      await handlePayWithCard(amountAmd);
      return;
    }

    if (!formData.email) { toast(t("pages.donate.alertEmail")); return; }
    // No fake "success" here: we have no way of knowing whether the user actually
    // completes the payment on Buy Me a Coffee's site, so we only claim to have
    // opened the payment page — never that the donation itself succeeded.
    window.open("https://buymeacoffee.com/greenwich.am", "_blank");
    setDonationSuccess(true);
  };

  const handleReopenPayment = () => {
    window.open("https://buymeacoffee.com/greenwich.am", "_blank");
  };

  const handleBackToForm = () => {
    setDonationSuccess(false); setSelectedTier(null); setPaymentMethod(""); setDonationAmount(""); setCustomAmount(""); setFormData({ name: "", email: "", message: "" });
  };

  const totalAmount = donationAmount ? parseFloat(donationAmount) : 0;
  const totalInAMD = totalAmount * 416.67;

  const focusInput = (e) => { e.target.style.borderColor = "#ffcb47"; e.target.style.boxShadow = "0 0 0 3px rgba(255,203,71,0.15)"; };
  const blurInput = (e) => { e.target.style.borderColor = "rgba(255,255,255,0.18)"; e.target.style.boxShadow = "none"; };

  return (
    <div className="donate-container" style={{ minHeight: "100vh", fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif", overflowX: "hidden" }}>
      <SEO
        titleKey="pages.seo.donate.title"
        descriptionKey="pages.seo.donate.description"
        keywordsKey="pages.seo.donate.keywords"
        path="/donate"
        breadcrumbLabel="Donate"
      />
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&display=swap');
        html,body{overflow-x:hidden;} * { box-sizing: border-box; }
      `}</style>

      {/* ── HERO ── */}
      <div style={{ position: "relative", padding: "clamp(44px,8vw,60px) 16px 40px", textAlign: "center", overflow: "hidden", zIndex: 1 }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", position: "relative", zIndex: 2 }}>

          <h1 className="donate-hero-title" style={{ fontSize: 40, fontWeight: 800, marginBottom: 14, lineHeight: 1.15, marginTop: 36, color: "#fdfdf9", textShadow: "0 2px 18px rgba(0,0,0,0.25)" }}>{t("pages.donate.heroTitle")}</h1>
          <p className="donate-hero-sub" style={{ fontSize: 18, opacity: 0.85, maxWidth: 640, margin: "0 auto 32px", lineHeight: 1.65, color: "rgba(234,243,238,0.78)" }}>
            {t("pages.donate.heroSub")}
          </p>

          <div className="donate-hero-img-frame" style={{ width: "100%", maxWidth: "min(820px, 100%)", margin: "0 auto", borderRadius: 20, overflow: "hidden", padding: 8 }}>
            <img className="donate-hero-img" src="/nkarner/donate-hero.jpg" alt="Armenian Youth Programs"
              style={{ width: "100%", height: 340, objectFit: "cover", display: "block", borderRadius: 14 }}
              onError={e => { e.target.src = "/nkarner/Screenshot 2026-01-14 165120.png"; }} />
          </div>
        </div>
      </div>

      {/* ── MAIN ── */}
      <div className="donate-main" style={{ maxWidth: 1200, margin: "0 auto", padding: "0 clamp(10px,3vw,20px) 40px", position: "relative", zIndex: 3 }}>
        <div className="donate-grid">

          {/* ── LEFT ── */}
          <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>

            {/* Mission card */}
            <div className="donate-card donate-glass" style={{ borderRadius: 20, padding: 32, animation: "donateFadeUp .5s ease" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 24 }}>
                <div style={{ background: "linear-gradient(150deg, #ffd873, #d69a17 65%, #a6740f)", width: 52, height: 52, borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, boxShadow: "0 10px 30px rgba(214,154,23,0.45)" }}>
                  <Heart size={26} color="#2a2005" />
                </div>
                <div>
                  <h2 className="section-h2" style={{ fontSize: 24, fontWeight: 700, color: "#fdfdf9", margin: "0 0 4px" }}>{t("pages.donate.missionTitle")}</h2>
                  <p style={{ color: "rgba(234,243,238,0.6)", fontSize: 14, margin: 0 }}>{t("pages.donate.missionSub")}</p>
                </div>
              </div>

              <div className="donate-subpanel" style={{ padding: "20px 22px", borderRadius: 12, marginBottom: 24, borderLeft: "4px solid #ffcb47" }}>
                <p style={{ fontSize: 16, lineHeight: 1.65, color: "#fdfdf9", marginBottom: 10, fontStyle: "italic" }}>
                  {t("pages.donate.quote")}
                </p>
                <p style={{ fontSize: 14, lineHeight: 1.7, color: "rgba(234,243,238,0.7)", margin: 0 }}>
                  {t("pages.donate.quoteBody")}
                </p>
              </div>

              <div className="stats-grid">
                {[
                  { icon: <Users size={28} color="#5aa8ff" />, val: "3,500+", label: t("pages.donate.statYouth") },
                  { icon: <Target size={28} color="#ff6fa5" />, val: "85%", label: t("pages.donate.statSuccess") },
                  { icon: <Award size={28} color="#5ecb7c" />, val: "3,300+", label: t("pages.donate.statPrograms") },
                ].map((s, i) => (
                  <div key={i} className="donate-subpanel" style={{ textAlign: "center", padding: "18px 12px", borderRadius: 12 }}>
                    <div style={{ marginBottom: 8 }}>{s.icon}</div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: "#fdfdf9", marginBottom: 4 }}>{s.val}</div>
                    <div style={{ fontSize: 12, color: "rgba(234,243,238,0.6)" }}>{s.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Tiers card */}
            <div className="donate-card donate-glass" style={{ borderRadius: 20, padding: 32 }}>
              <h3 className="section-h2" style={{ fontSize: 22, fontWeight: 700, color: "#fdfdf9", textAlign: "center", marginBottom: 6 }}>{t("pages.donate.chooseTier")}</h3>
              <p style={{ textAlign: "center", color: "rgba(234,243,238,0.6)", fontSize: 14, marginBottom: 28 }}>{t("pages.donate.chooseTierSub")}</p>

              <div className="tiers-grid">
                {donationTiers.map(tier => (
                  <div key={tier.id} className="tier-card"
                    onClick={() => handleTierSelect(tier)}
                    style={{ border: selectedTier === tier.id ? `2px solid ${tier.color}` : "1px solid rgba(255,255,255,0.14)", background: selectedTier === tier.id ? `${tier.color}22` : "var(--fg-glass-fill)", transform: selectedTier === tier.id ? "translateY(-4px)" : "none", boxShadow: selectedTier === tier.id ? `0 8px 24px ${tier.color}40` : "0 2px 8px rgba(0,0,0,0.18)" }}>
                    {tier.popular && (
                      <div style={{ position: "absolute", top: -11, left: "50%", transform: "translateX(-50%)", background: tier.color, color: "#0f1e17", padding: "3px 12px", borderRadius: 12, fontSize: 11, fontWeight: 700, whiteSpace: "nowrap" }}>
                        ★ {t("pages.donate.popular")}
                      </div>
                    )}
                    <div style={{ color: tier.color, fontSize: 26, fontWeight: 800, marginBottom: 2 }}>${tier.amount}</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#fdfdf9", marginBottom: 2 }}>{tier.name}</div>
                    <div style={{ fontSize: 11, color: "rgba(234,243,238,0.5)", marginBottom: 12 }}>{t("pages.donate.perMonth")}</div>
                    <div style={{ fontSize: 12, color: "rgba(234,243,238,0.65)", marginBottom: 12, minHeight: 32, lineHeight: 1.4 }}>{tier.description}</div>
                    <ul style={{ listStyle: "none", padding: 0, margin: 0, fontSize: 11, color: "rgba(234,243,238,0.7)", textAlign: "left" }}>
                      {tier.perks.map((perk, i) => (
                        <li key={i} style={{ marginBottom: 5, display: "flex", alignItems: "flex-start", gap: 5 }}>
                          <span style={{ color: tier.color, flexShrink: 0, fontWeight: 700 }}>✓</span> {perk}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              {/* Custom amount */}
              <div className="donate-subpanel" style={{ padding: "22px 20px", borderRadius: 12, marginTop: 22 }}>
                <h4 style={{ fontSize: 16, fontWeight: 600, color: "#fdfdf9", textAlign: "center", marginBottom: 14 }}>{t("pages.donate.customAmount")}</h4>
                <div style={{ display: "flex", alignItems: "center", gap: 8, maxWidth: 360, margin: "0 auto", flexWrap: "wrap", justifyContent: "center" }}>
                  <div style={{ position: "relative", flex: 1, minWidth: 140 }}>
                    <DollarSign size={17} style={{ position: "absolute", left: 13, top: "50%", transform: "translateY(-50%)", color: "rgba(234,243,238,0.5)" }} />
                    <input type="text" value={customAmount} onChange={handleCustomAmountChange} placeholder={`Min $${MINIMUM_AMOUNT_USD}`}
                      className="donate-input" style={{ paddingLeft: 38 }} onFocus={focusInput} onBlur={blurInput} />
                  </div>
                  <div style={{ fontSize: 13, color: "rgba(234,243,238,0.6)", whiteSpace: "nowrap" }}>
                    ≈ {(customAmount ? parseFloat(customAmount) * 416.67 : 0).toLocaleString()} AMD
                  </div>
                </div>
                <div style={{ textAlign: "center", marginTop: 10, fontSize: 12, color: "rgba(234,243,238,0.45)" }}>
                  {t("pages.donate.minUsd", { amount: MINIMUM_AMOUNT_USD, amd: MINIMUM_AMOUNT_AMD.toLocaleString() })}
                </div>
              </div>
            </div>
          </div>

          {/* ── RIGHT ── */}
          <div className="sticky-panel">
            {paymentResult === "success" && (
              <div className="donate-glass" style={{ borderRadius: 14, padding: "14px 18px", marginBottom: 14, borderLeft: "4px solid #5ecb7c" }}>
                {t("pages.donate.cardPaymentSuccess")}
              </div>
            )}
            {paymentResult === "failed" && (
              <div className="donate-glass" style={{ borderRadius: 14, padding: "14px 18px", marginBottom: 14, borderLeft: "4px solid #ff6b6b" }}>
                {t("pages.donate.cardPaymentFailed")}
              </div>
            )}
            {paymentResult === "error" && (
              <div className="donate-glass" style={{ borderRadius: 14, padding: "14px 18px", marginBottom: 14, borderLeft: "4px solid #ff6b6b" }}>
                {t("pages.donate.cardPaymentError")}
              </div>
            )}
            <div className="donate-glass" style={{ borderRadius: 20, padding: 24, marginBottom: 16 }}>
              {donationSuccess ? (
                <div style={{ textAlign: "center", padding: "32px 16px", animation: "donateFadeUp .4s ease" }}>
                  <div style={{ width: 70, height: 70, background: "linear-gradient(150deg, #ffd873, #d69a17 65%, #a6740f)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 18px", animation: "donateScaleIn .5s ease", boxShadow: "0 10px 30px rgba(214,154,23,0.45)" }}>
                    <Heart size={32} color="#2a2005" fill="#2a2005" />
                  </div>
                  <h3 style={{ fontSize: 22, fontWeight: 700, color: "#fdfdf9", marginBottom: 12 }}>{t("pages.donate.redirectTitle")}</h3>
                  <p style={{ fontSize: 15, color: "rgba(234,243,238,0.7)", lineHeight: 1.6, marginBottom: 20 }}>
                    {t("pages.donate.redirectBody", { amount: totalAmount.toFixed(2) })}
                  </p>
                  <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
                    <button onClick={handleReopenPayment}
                      style={{ padding: "10px 22px", background: "linear-gradient(150deg, #ffd873, #d69a17 65%, #a6740f)", color: "#2a2005", border: "none", borderRadius: 24, fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                      {t("pages.donate.reopenPayment")}
                    </button>
                    <button onClick={handleBackToForm}
                      style={{ padding: "10px 22px", background: "transparent", color: "#ffd873", border: "1px solid rgba(255,203,71,0.5)", borderRadius: 24, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                      {t("pages.donate.backToForm")}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {/* Order summary */}
                  <div className="donate-subpanel" style={{ padding: 20, borderRadius: 12, marginBottom: 22 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                      <span style={{ fontSize: 14, color: "rgba(234,243,238,0.7)", fontWeight: 500 }}>{t("pages.donate.supportLevel")}</span>
                      <span style={{ fontSize: 16, fontWeight: 700, color: "#fdfdf9" }}>
                        {selectedTier ? donationTiers.find(tier => tier.id === selectedTier)?.name : donationAmount ? t("pages.donate.custom") : "—"}
                      </span>
                    </div>

                    <div style={{ marginBottom: 14 }}>
                      <div style={{ fontSize: 13, color: "rgba(234,243,238,0.6)", marginBottom: 8 }}>{t("pages.donate.quickAmount")}</div>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        {[3, 5, 10, 25].map(amt => (
                          <button key={amt} type="button" className={`quick-amt-btn${totalAmount === amt ? " active" : ""}`}
                            onClick={() => { const t = donationTiers.find(t => t.amount === amt); t ? handleTierSelect(t) : (setSelectedTier(null), setDonationAmount(amt.toString()), setCustomAmount("")); }}>
                            ${amt}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                      <span style={{ fontSize: 14, color: "rgba(234,243,238,0.7)", fontWeight: 500 }}>{t("pages.donate.monthly")}</span>
                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontSize: 28, fontWeight: 800, color: "#ffcb47", lineHeight: 1 }}>${totalAmount > 0 ? totalAmount.toFixed(2) : "0.00"}</div>
                        <div style={{ fontSize: 12, color: "rgba(234,243,238,0.5)", marginTop: 2 }}>≈ {totalInAMD.toLocaleString()} AMD</div>
                      </div>
                    </div>

                    <div style={{ height: 1, background: "rgba(255,255,255,0.12)", margin: "14px 0" }} />
                    <div style={{ fontSize: 12, color: "rgba(234,243,238,0.5)", display: "flex", flexDirection: "column", gap: 5 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 7 }}><Shield size={12} /> {t("pages.donate.recurringNote")}</div>
                      <div style={{ display: "flex", alignItems: "center", gap: 7 }}><CheckCircle size={12} /> {t("pages.donate.cancelAnytime")}</div>
                    </div>
                  </div>

                  {/* Donor info */}
                  <div style={{ marginBottom: 22 }}>
                    <h4 style={{ fontSize: 16, fontWeight: 700, color: "#fdfdf9", marginBottom: 14, paddingBottom: 10, borderBottom: "1px solid rgba(255,255,255,0.12)" }}>{t("pages.donate.yourInfo")}</h4>
                    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                      <input type="text" name="name" value={formData.name} onChange={handleInputChange} placeholder={t("pages.donate.fullName")} className="donate-input" onFocus={focusInput} onBlur={blurInput} />
                      <input type="email" name="email" value={formData.email} onChange={handleInputChange} placeholder={t("pages.donate.emailReq")} required className="donate-input" onFocus={focusInput} onBlur={blurInput} />
                    </div>
                  </div>

                  {/* Payment method */}
                  <div style={{ marginBottom: 22 }}>
                    <h4 style={{ fontSize: 16, fontWeight: 700, color: "#fdfdf9", marginBottom: 14, paddingBottom: 10, borderBottom: "1px solid rgba(255,255,255,0.12)" }}>{t("pages.donate.paymentMethod")}</h4>
                    <button type="button" onClick={() => handlePaymentMethodSelect("buymeacoffee")}
                      className={`donate-payment-btn${paymentMethod === "buymeacoffee" ? " active" : ""}`}>
                      <div style={{ width: 44, height: 44, borderRadius: 10, background: "#ffdd00", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 22 }}>☕</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 15, fontWeight: 600, color: "#fdfdf9" }}>{t("pages.donate.bmcTitle")}</div>
                        <div style={{ fontSize: 12, color: "rgba(234,243,238,0.55)", marginTop: 2 }}>{t("pages.donate.bmcSub")}</div>
                      </div>
                      {paymentMethod === "buymeacoffee" && <CheckCircle size={20} color="#ffcb47" />}
                    </button>

                    {vposEnabled && (
                      <button type="button" onClick={() => setPaymentMethod("card")}
                        className={`donate-payment-btn${paymentMethod === "card" ? " active" : ""}`} style={{ marginTop: 10 }}>
                        <div style={{ width: 44, height: 44, borderRadius: 10, background: "#5aa8ff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          <CreditCard size={22} color="#0f1e17" />
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 15, fontWeight: 600, color: "#fdfdf9" }}>{t("pages.donate.cardTitle")}</div>
                          <div style={{ fontSize: 12, color: "rgba(234,243,238,0.55)", marginTop: 2 }}>{t("pages.donate.cardSub")}</div>
                        </div>
                        {paymentMethod === "card" && <CheckCircle size={20} color="#ffcb47" />}
                      </button>
                    )}
                  </div>

                  {/* Submit */}
                  <button className="submit-btn" onClick={handleSubmit}
                    disabled={!donationAmount || parseFloat(donationAmount) < MINIMUM_AMOUNT_USD || !paymentMethod || cardPaying}
                    style={{ background: (!donationAmount || parseFloat(donationAmount) < MINIMUM_AMOUNT_USD || !paymentMethod || cardPaying) ? "rgba(255,255,255,0.08)" : "linear-gradient(150deg, #ffd873, #d69a17 65%, #a6740f)", color: (!donationAmount || parseFloat(donationAmount) < MINIMUM_AMOUNT_USD || !paymentMethod || cardPaying) ? "rgba(234,243,238,0.6)" : "#2a2005", boxShadow: (!donationAmount || parseFloat(donationAmount) < MINIMUM_AMOUNT_USD || !paymentMethod || cardPaying) ? "none" : "0 4px 14px rgba(214,154,23,0.35)", opacity: 1 }}>
                    <Heart size={18} fill="currentColor" />{" "}
                    {paymentMethod === "card"
                      ? (cardPaying ? t("pages.donate.redirectingToBank") : t("pages.donate.continueCard"))
                      : t("pages.donate.continueBmc")}
                  </button>
                  {paymentMethod === "card" && !user && (
                    <div style={{ marginTop: 10, fontSize: 12, color: "rgba(255,160,160,0.9)", textAlign: "center" }}>
                      {t("pages.donate.alertCardLogin")}
                    </div>
                  )}

                  <div style={{ marginTop: 14, fontSize: 11, color: "rgba(234,243,238,0.45)", textAlign: "center", lineHeight: 1.6 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 4 }}>
                      <Shield size={11} /><Lock size={11} /> {t("pages.donate.secureNote")}
                    </div>
                    <p style={{ margin: 0 }}>
                      {t("pages.donate.termsAgree")}{" "}
                      <a href="#" className="donate-link">{t("pages.donate.terms")}</a>
                      {" "}{t("pages.donate.and")}{" "}
                      <a href="#" className="donate-link">{t("pages.donate.privacyPolicy")}</a>.
                    </p>
                  </div>
                </>
              )}
            </div>

            {/* Why support */}
            <div className="donate-glass" style={{ borderRadius: 20, padding: 22 }}>
              <h4 style={{ fontSize: 16, fontWeight: 700, color: "#ffd873", marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
                <Star size={17} /> {t("pages.donate.whyMonthly")}
              </h4>
              <ul className="why-list" style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {Array.isArray(whySupportItems) && whySupportItems.map((row, i) => (
                  <li key={i}><span style={{ color: "#5ecb7c", flexShrink: 0, fontWeight: 700 }}>✓</span> <span><strong>{row[0]}</strong>: {row[1]}</span></li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>


    </div>
  );
};

export default Donate;
